import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "../EmailBuilderConfirmProvider";
import { findBlock } from "../../domain/find";
import { blurPanelField, focusAndRevealSelection } from "../../domain/reveal-selection";
import {
  adjacentCanvasTabSelection,
  escapeCanvasSelection,
} from "../../domain/selection/navigation";
import { getSelectionCapabilities } from "../../domain/selection/policy";
import { getMultiSelectRowCapabilities } from "../../domain/selection/row-bulk-ops";
import { selectAllBlocksInContext } from "../../domain/selection/block-selection-gesture";
import { getEditorState, useEditorActions } from "../../domain/redux/hooks";
import { deleteSelectionWithConfirm } from "../../lib/deleteSelection";
import { ungroupSelectionWithConfirm } from "../../lib/ungroupSelection";
import { resolveEmailBuilderBlockClipboardHotkey } from "../emailBuilderBlockClipboardHotkeys";
import {
  resolveEmailBuilderCanvasReorderHotkeyDirection,
  shouldBlurEmailBuilderPanelFieldOnEscape,
  shouldHandleEmailBuilderCanvasKeys,
  shouldPreventEmailBuilderTextEditableBrowserShortcutLeak,
  shouldSkipEmailBuilderCanvasHotkeysForTarget,
} from "../emailBuilderCanvasHotkeys";
import { resolveEmailBuilderSelectAllHotkey } from "../emailBuilderSelectionHotkeys";
import { resolveEmailBuilderRowGroupHotkey } from "../emailBuilderRowGroupHotkeys";

export function useEmailBuilderCanvasHotkeys(mode: "design" | "preview"): void {
  const {
    setSelection,
    setBlockSelection,
    duplicateSelected,
    deleteSelected,
    deleteGroup,
    groupRows,
    ungroup,
    moveSelected,
    copySelectedBlocksToClipboard,
    cutSelectedBlocksToClipboard,
    pasteBlocksFromEditorClipboard,
    setTextEditingBlock,
    setTextEditReentryGuard,
  } = useEditorActions();
  const { confirm } = useConfirm();
  const { t } = useTranslation();

  useEffect(() => {
    if (mode !== "design") {
      return;
    }

    function handleKeydown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (shouldBlurEmailBuilderPanelFieldOnEscape(event.target)) {
          event.preventDefault();
          event.stopPropagation();
          blurPanelField();
          return;
        }

        const {
          textEditingBlock,
          selection,
        } = getEditorState();

        if (textEditingBlock) {
          event.preventDefault();
          event.stopPropagation();
          setTextEditingBlock(null);
          if (globalThis.document.activeElement instanceof HTMLElement) {
            globalThis.document.activeElement.blur();
          }
          const blockSelection = {
            kind: "block" as const,
            rowId: textEditingBlock.rowId,
            columnId: textEditingBlock.columnId,
            blockId: textEditingBlock.blockId,
          };
          setSelection(blockSelection);
          focusAndRevealSelection(blockSelection, { pulse: false });
          return;
        }

        if (selection.kind !== "email") {
          event.preventDefault();
          event.stopPropagation();
          const next = escapeCanvasSelection(selection);
          setSelection(next);
          focusAndRevealSelection(next, { pulse: false });
        }
        return;
      }

      if (shouldSkipEmailBuilderCanvasHotkeysForTarget(event.target)) {
        if (shouldPreventEmailBuilderTextEditableBrowserShortcutLeak(event)) {
          event.preventDefault();
        }
        return;
      }

      const selectAllAction = resolveEmailBuilderSelectAllHotkey(event);
      if (selectAllAction === "select-all") {
        const {
          document,
          selection,
          selectedBlockIds,
          primaryBlockId,
          anchorBlockId,
        } = getEditorState();
        if (!shouldHandleEmailBuilderCanvasKeys(event.target, selection)) {
          return;
        }
        event.preventDefault();
        const next = selectAllBlocksInContext(document, {
          selectedBlockIds,
          primaryBlockId,
          anchorBlockId,
        });
        if (next.selectedBlockIds.length > 0) {
          setBlockSelection(next);
        }
        return;
      }

      if (event.key === "Tab" && shouldHandleEmailBuilderCanvasKeys(event.target, getEditorState().selection)) {
        const { document, selection } = getEditorState();
        event.preventDefault();
        blurPanelField();
        const direction = event.shiftKey ? -1 : 1;
        const next = adjacentCanvasTabSelection(document, selection, direction);
        setSelection(next);
        focusAndRevealSelection(next, { pulse: false });
        return;
      }

      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        shouldHandleEmailBuilderCanvasKeys(event.target, getEditorState().selection)
      ) {
        const {
          document,
          selection,
          textEditingBlock,
        } = getEditorState();

        if (!textEditingBlock && selection.kind === "block") {
          const block = findBlock(
            document,
            selection.rowId,
            selection.columnId,
            selection.blockId,
          );
          if (block?.type === "text") {
            event.preventDefault();
            const target = {
              rowId: selection.rowId,
              columnId: selection.columnId,
              blockId: selection.blockId,
            };
            setTextEditingBlock(target);
            setTextEditReentryGuard(null);
          }
        }
        return;
      }

      if (event.key === "Delete" || event.key === "Backspace") {
        const { document, selection, selectedRowIds } = getEditorState();
        if (selection.kind === "row" && selectedRowIds.length > 1) {
          const rowCapabilities = getMultiSelectRowCapabilities(
            document,
            selectedRowIds,
          );
          if (!rowCapabilities?.canDelete) {
            return;
          }
          event.preventDefault();
          deleteSelected();
          return;
        }

        const capabilities = getSelectionCapabilities(document, selection);
        if (!capabilities?.canDelete) {
          return;
        }
        event.preventDefault();
        void deleteSelectionWithConfirm(
          document,
          selection,
          { confirm },
          { deleteSelected, deleteGroup },
          t,
        );
        return;
      }

      const { document, selection } = getEditorState();
      const reorderAxis = selection.kind === "column" ? "horizontal" : "vertical";
      const reorderDirection = resolveEmailBuilderCanvasReorderHotkeyDirection(
        event,
        reorderAxis,
      );
      if (reorderDirection !== null) {
        if (!shouldHandleEmailBuilderCanvasKeys(event.target, selection)) {
          return;
        }
        const capabilities = getSelectionCapabilities(document, selection);
        if (!capabilities?.showMoveButtons) {
          return;
        }
        if (reorderDirection === -1 && !capabilities.canUp) {
          return;
        }
        if (reorderDirection === 1 && !capabilities.canDown) {
          return;
        }
        event.preventDefault();
        moveSelected(reorderDirection);
        return;
      }

      const clipboardAction = resolveEmailBuilderBlockClipboardHotkey(event);
      if (clipboardAction !== null) {
        const { document, selection, blockClipboard } = getEditorState();
        if (!shouldHandleEmailBuilderCanvasKeys(event.target, selection)) {
          return;
        }

        if (clipboardAction === "copy" || clipboardAction === "cut") {
          if (selection.kind !== "block") {
            return;
          }
          const capabilities = getSelectionCapabilities(document, selection);
          if (clipboardAction === "copy") {
            if (!capabilities?.canDuplicate) {
              return;
            }
            event.preventDefault();
            copySelectedBlocksToClipboard();
            return;
          }
          if (!capabilities?.canDelete) {
            return;
          }
          event.preventDefault();
          cutSelectedBlocksToClipboard();
          return;
        }

        if (blockClipboard.length === 0 || selection.kind !== "block") {
          return;
        }
        event.preventDefault();
        pasteBlocksFromEditorClipboard();
        return;
      }

      const rowGroupAction = resolveEmailBuilderRowGroupHotkey(event);
      if (rowGroupAction === "group") {
        const { document, selectedRowIds } = getEditorState();
        const rowCapabilities = getMultiSelectRowCapabilities(
          document,
          selectedRowIds,
        );
        if (!rowCapabilities?.canGroup) {
          return;
        }
        event.preventDefault();
        groupRows([...selectedRowIds]);
        return;
      }

      if (rowGroupAction === "ungroup") {
        const { document, selection } = getEditorState();
        if (selection.kind !== "group") {
          return;
        }
        const capabilities = getSelectionCapabilities(document, selection);
        if (!capabilities?.canUngroup) {
          return;
        }
        event.preventDefault();
        void ungroupSelectionWithConfirm(
          document,
          selection,
          { confirm },
          { ungroup },
          t,
        );
        return;
      }

      const withMeta = event.metaKey || event.ctrlKey;
      if (!withMeta) {
        return;
      }

      if (event.key.toLowerCase() === "d") {
        const { document, selection, selectedRowIds } = getEditorState();
        if (selection.kind === "row" && selectedRowIds.length > 1) {
          const rowCapabilities = getMultiSelectRowCapabilities(
            document,
            selectedRowIds,
          );
          if (!rowCapabilities?.canDuplicate) {
            return;
          }
        }
        event.preventDefault();
        duplicateSelected();
      }
    }

    window.document.addEventListener("keydown", handleKeydown);
    return () => window.document.removeEventListener("keydown", handleKeydown);
  }, [
    confirm,
    copySelectedBlocksToClipboard,
    cutSelectedBlocksToClipboard,
    deleteGroup,
    deleteSelected,
    duplicateSelected,
    groupRows,
    moveSelected,
    ungroup,
    mode,
    pasteBlocksFromEditorClipboard,
    setBlockSelection,
    setSelection,
    setTextEditReentryGuard,
    setTextEditingBlock,
    t,
  ]);
}
